/**
 * Description: Unified product form component supporting both full tabbed interface and inline quick-create mode
 */

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Product } from '@services/api'
import SystemsCatalog from '@components/SystemsCatalog'
import ProductDetailsFields from '@components/ProductDetailsFields'

interface ProductFormProps {
  product?: Product
  onSubmit: (data: Product) => void
  onCancel: () => void
  isLoading?: boolean
  // Inline mode props
  inline?: boolean
  systemId?: number
  autoLink?: boolean
  onProductCreated?: () => void
}

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onSubmit,
  onCancel,
  isLoading = false,
  inline = false,
  systemId,
  autoLink = true,
  onProductCreated,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'systems'>('details')
  const [selectedSystemIds, setSelectedSystemIds] = useState<number[]>([])
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Product>({
    mode: 'onChange',
    defaultValues: product || { status: 'active' },
  })

  useEffect(() => {
    if (product) {
      reset(product)
    }
  }, [product, reset])

  const handleFormSubmit = async (data: Product) => {
    // Include selected system IDs when creating a new product
    const productData = {
      ...data,
      systemIds: !product?.id ? selectedSystemIds : undefined,
    }

    await onSubmit(productData)

    // If inline mode and we have callbacks, handle them
    if (inline && onProductCreated) {
      reset({ status: 'active' }) // Reset form after creation in inline mode
      setSelectedSystemIds([]) // Clear selected systems
      onProductCreated()
    }
  }

  // Inline mode - simplified form
  if (inline) {
    return (
      <div className="space-y-3 rounded-lg bg-gray-50 p-4 dark:bg-white/5">
        <ProductDetailsFields register={register} errors={errors} compact />
        <div className="flex gap-2">
          <button type="button" onClick={handleSubmit(handleFormSubmit)} className="btn-primary" disabled={isLoading}>
            {systemId && autoLink ? 'Create & Link' : 'Create Product'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={isLoading}>
              Cancel
            </button>
          )}
        </div>
      </div>
    )
  }

  // Full mode - tabbed interface

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Tab Navigation */}
      <div className="overflow-x-auto border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-8" aria-label="Tabs">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'cursor-pointer border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            Product Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('systems')}
            className={`border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
              activeTab === 'systems'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'cursor-pointer border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            } `}
          >
            Systems
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <ProductDetailsFields register={register} errors={errors} />
        </div>
      )}

      {/* Systems Tab */}
      {activeTab === 'systems' && (
        <div className="py-4">
          <SystemsCatalog
            productId={product?.id}
            productName={product?.product_name || 'New Product'}
            onSystemsChange={!product?.id ? setSelectedSystemIds : undefined}
            selectedSystemIds={selectedSystemIds}
          />
        </div>
      )}

      {/* Form Actions - only visible in details tab or when creating a new product */}
      {(activeTab === 'details' || !product) && (
        <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
          <button type="button" onClick={onCancel} className="btn-secondary cursor-pointer" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary cursor-pointer" disabled={isLoading}>
            {isLoading ? 'Saving...' : product?.id ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      )}
    </form>
  )
}

export default ProductForm
